import React, { useState, useEffect } from "react";
import {
    Table, TableHead, TableRow, TableCell, TableBody, Button,
} from "@mui/material";

import useAuthToken from '../functions/useAuthToken';
import type { OrganisationUserDto } from "../models/OrganisationUsers";
import { useOrganisationsApi } from "../functions/OrganisationsApi";
import OrganisationUserDialog from "./OrganisationUserDialog";

const OrganisationUsersPanel = ({ organisationId }: { organisationId: number }) => {

    const getAccessToken = useAuthToken();
    const { getUsers } = useOrganisationsApi();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<OrganisationUserDto[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<OrganisationUserDto | null>(null);

    const load = async () => {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        setLoading(true);
        try {
            const res = await getUsers(organisationId, accessToken);
            setUsers(res);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [organisationId]);

    const handleAdd = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const handleEdit = (user: OrganisationUserDto) => {
        setEditing(user);
        setDialogOpen(true);
    };

    return (
        <>
            <div className="section-toolbar">
                <h3>Associated Users</h3>
                <Button variant="contained" onClick={handleAdd}>
                    Add User
                </Button>
            </div>

            {loading ? (
                <div className="panel-loading">Loading users…</div>
            ) : users.length === 0 ? (
                <div className="panel-empty">No users associated with this organisation.</div>
            ) : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Login</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((u) => (
                        <TableRow key={u.id}>
                            <TableCell>{u.displayName}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>{u.roleName}</TableCell>
                            <TableCell>{u.isActive ? "Active" : "Inactive"}</TableCell>
                            <TableCell>{u.hasLogin ? "Enabled" : "Contact only"}</TableCell>
                            <TableCell align="right">
                                <Button size="small" onClick={() => handleEdit(u)}>
                                    Edit
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            )}

            <OrganisationUserDialog
                open={dialogOpen || !!editing}
                organisationId={organisationId}
                user={editing}
                onClose={() => { setDialogOpen(false); setEditing(null); }}
                onSaved={async () => {
                    setDialogOpen(false);
                    setEditing(null);
                    await load();
                }}
            />
        </>
    );
};

export default OrganisationUsersPanel;